import type { VideoUrls } from '@/utils/hooks/video';

export const mediaSignedUrl = async (
  storageKey: string
): Promise<string | null> => {
  const response = await fetch(
    `/api/media/url?key=${encodeURIComponent(storageKey)}`
  );

  if (!response.ok) {
    console.error('Failed to fetch media URL', await response.text());
    return null;
  }

  const data = await response.json();
  return data.url;
};

export const uploadFilePresignedS3 = async (
  file: File
): Promise<
  | { success: true; storageKey: string; mediaType: number; fileSizeMB: number }
  | { success: false; error: string }
> => {
  try {
    const fileSizeMB = file.size / 1024 / 1024;
    const presignRes = await fetch('/api/media/presign', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type,
        fileSizeMB
      })
    });
    const presignData = await presignRes.json();
    if (!presignRes.ok || !presignData.success) {
      return {
        success: false,
        error: presignData.error || 'Failed to get upload URL'
      };
    }
    const uploadRes = await fetch(presignData.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });
    if (!uploadRes.ok) {
      return { success: false, error: 'Failed to upload file to S3' };
    }
    return {
      success: true,
      storageKey: presignData.storageKey,
      mediaType: presignData.mediaType,
      fileSizeMB
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Upload failed'
    };
  }
};

export type RecordingKind = 'conversation' | 'clip' | 'reel';

/**
 * The signed playback and thumbnail URLs for a conversation, clip or reel
 * recording, from the matching `/api/<kind>/<id>/recording-url` route. A
 * failed request is logged and resolves to nulls, so the player falls back to
 * its empty state rather than throwing.
 */
export const recordingUrls = async (
  kind: RecordingKind,
  id: number
): Promise<VideoUrls> => {
  const response = await fetch(`/api/${kind}/${id}/recording-url`);

  if (!response.ok) {
    console.error(
      `Failed to fetch ${kind} recording URL`,
      await response.text()
    );
    return { recordingUrl: null, thumbnailUrl: null };
  }

  const data = await response.json();
  return { recordingUrl: data.recordingUrl, thumbnailUrl: data.thumbnailUrl };
};

export interface ReelBookmark {
  time: number;
  label: string;
}

export interface ClipWithIndex {
  id: number;
  name: string | null;
  duration: number;
  from_start: number;
  index?: number;
}

/**
 * Generates bookmarks for a reel video player from an ordered array of clips.
 *
 * This function:
 * - Orders clips by their index (from reel_clips.index)
 * - Calculates cumulative timestamps as clips are stitched together
 * - Converts milliseconds to seconds
 * - Only includes clips that have a name
 *
 * @param clips - Array of clips, optionally with an index property from reel_clips
 * @returns Array of bookmarks with time (seconds) and label (clip name)
 *
 * @example
 * const clips = [
 *   { id: 1, name: "Opening", duration: 10000, from_start: 0, index: 0 },
 *   { id: 2, name: "Question 1", duration: 5000, from_start: 15500, index: 1 },
 *   { id: 3, name: null, duration: 3000, from_start: 45200, index: 2 }, // Skipped - no name
 *   { id: 4, name: "Conclusion", duration: 8000, from_start: 120000, index: 3 }
 * ];
 *
 * const bookmarks = generateReelBookmarks(clips);
 * // Result: [
 * //   { time: 0, label: "Opening" },
 * //   { time: 10, label: "Question 1" },
 * //   { time: 18, label: "Conclusion" }
 * // ]
 */
export const generateReelBookmarks = (
  clips: ClipWithIndex[]
): ReelBookmark[] => {
  if (!clips || clips.length === 0) {
    return [];
  }

  const sortedClips = [...clips].sort((a, b) => {
    const indexA = a.index ?? 0;
    const indexB = b.index ?? 0;
    return indexA - indexB;
  });

  const bookmarks: ReelBookmark[] = [];
  let cumulativeTime = 0;

  for (const clip of sortedClips) {
    if (clip.name) {
      bookmarks.push({
        time: Math.floor(cumulativeTime / 1000),
        label: clip.name
      });
    }

    cumulativeTime += clip.duration;
  }

  return bookmarks;
};
